import { replaceAttachment, type GenieAttachment } from '@/types/voice/genie-attachment';

interface TranscriptMessage {
  message: string;
  isUser: boolean;
  timestamp: Date;
  timeInCallSecs?: number;
  attachment?: GenieAttachment;
}

interface StoredTranscript {
  conversationId: string;
  messages: TranscriptMessage[];
  startTime: number;
  lastUpdated: number;
}

export class TranscriptStorageService {
  private static readonly STORAGE_KEY_PREFIX = 'transcript_';
  private static readonly MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

  /**
   * Store transcript for a conversation in localStorage
   */
  static storeTranscript(conversationId: string, messages: TranscriptMessage[]): void {
    try {
      const storageKey = this.getStorageKey(conversationId);
      const transcriptData: StoredTranscript = {
        conversationId,
        messages: messages.map(msg => ({
          ...msg,
          timestamp: msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp)
        })),
        startTime: Date.now(),
        lastUpdated: Date.now(),
      };

      localStorage.setItem(storageKey, JSON.stringify(transcriptData));
    } catch (error) {
      console.warn('Failed to store transcript in localStorage:', error);
    }
  }

  /**
   * Add a single message to existing transcript storage
   */
  static addMessage(conversationId: string, message: TranscriptMessage, conversationStartTime?: number): void {
    try {
      const existingData = this.getStoredTranscript(conversationId);
      const messages = existingData ? existingData.messages : [];
      
      // Calculate time in call if conversation start time is available
      const messageWithTime = {
        ...message,
        timeInCallSecs: conversationStartTime ? 
          Math.floor((message.timestamp.getTime() - conversationStartTime) / 1000) : 
          undefined
      };

      messages.push(messageWithTime);

      const transcriptData: StoredTranscript = {
        conversationId,
        messages,
        startTime: existingData?.startTime || Date.now(),
        lastUpdated: Date.now(),
      };

      const storageKey = this.getStorageKey(conversationId);
      localStorage.setItem(storageKey, JSON.stringify(transcriptData));
    } catch (error) {
      console.warn('Failed to add message to transcript storage:', error);
    }
  }

  /**
   * Swap the stored card for `replacesUrl` for the one that took its place in the chat
   */
  static replaceAttachment(conversationId: string, replacesUrl: string, attachment: GenieAttachment): void {
    try {
      const existingData = this.getStoredTranscript(conversationId);
      if (!existingData) return;
      const transcriptData: StoredTranscript = {
        ...existingData,
        messages: replaceAttachment(existingData.messages, replacesUrl, attachment),
        lastUpdated: Date.now(),
      };
      localStorage.setItem(this.getStorageKey(conversationId), JSON.stringify(transcriptData));
    } catch (error) {
      console.warn('Failed to replace attachment in transcript storage:', error);
    }
  }

  /**
   * Message text and speaker only, for routing a live handoff to the right number
   */
  static getHandoffMessages(conversationId: string | null): Array<{ message: string; isUser: boolean }> {
    if (!conversationId) return [];
    return (this.getStoredTranscript(conversationId)?.messages ?? []).filter(({ attachment }) => !attachment).map(({ message, isUser }) => ({ message, isUser }));
  }

  /**
   * Retrieve stored transcript for a conversation
   */
  static getStoredTranscript(conversationId: string): StoredTranscript | null {
    try {
      const storageKey = this.getStorageKey(conversationId);
      const storedData = localStorage.getItem(storageKey);
      
      if (!storedData) {
        return null;
      }

      const transcriptData: StoredTranscript = JSON.parse(storedData);
      
      // Check if data is too old
      if (Date.now() - transcriptData.lastUpdated > this.MAX_AGE_MS) {
        this.removeTranscript(conversationId);
        return null;
      }

      // Parse timestamps back to Date objects
      transcriptData.messages = transcriptData.messages.map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));

      return transcriptData;
    } catch (error) {
      console.warn('Failed to retrieve stored transcript:', error);
      return null;
    }
  }

  /**
   * Convert stored transcript to ElevenLabs format
   */
  static convertToElevenLabsFormat(storedTranscript: StoredTranscript): any[] {
    return storedTranscript.messages.filter(msg => !msg.attachment).map(msg => ({
      role: msg.isUser ? 'user' : 'agent',
      message: msg.message,
      time_in_call_secs: msg.timeInCallSecs || 0
    }));
  }

  /**
   * Remove stored transcript for a conversation
   */
  static removeTranscript(conversationId: string): void {
    try {
      const storageKey = this.getStorageKey(conversationId);
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn('Failed to remove stored transcript:', error);
    }
  }

  /**
   * Clean up old transcript data
   */
  static cleanupOldTranscripts(): void {
    try {
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
          try {
            const data = JSON.parse(localStorage.getItem(key) || '{}');
            if (Date.now() - data.lastUpdated > this.MAX_AGE_MS) {
              keysToRemove.push(key);
            }
          } catch (error) {
            // If we can't parse the data, remove it
            keysToRemove.push(key);
          }
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      if (keysToRemove.length > 0) {
        console.log(`Cleaned up ${keysToRemove.length} old transcript entries`);
      }
    } catch (error) {
      console.warn('Failed to cleanup old transcripts:', error);
    }
  }

  private static getStorageKey(conversationId: string): string {
    return `${this.STORAGE_KEY_PREFIX}${conversationId}`;
  }

  /**
   * Initialize cleanup on service load
   */
  static initialize(): void {
    // Clean up old transcripts when service is first used
    this.cleanupOldTranscripts();
  }
}

// Initialize cleanup
TranscriptStorageService.initialize();